// hooks/useSensorData.js
import { useEffect, useMemo, useState } from 'react'
import { querySensorData } from '../services/influxService'

function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

/**
 * selectedSensorsByBuoy: { [buoyId]: string[] }
 * selectedFieldsByBuoy:  { [buoyId]: ['measurement:field'] }
 */
export function useSensorData(selectedBuoys, selectedSensorsByBuoy, selectedFieldsByBuoy, timeRange, timeStop = null) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const debouncedRange = useDebounced(timeRange, 400)
  const debouncedStop = useDebounced(timeStop, 400)

  const requestedTypes = useMemo(() => {
    const s = new Set()
    selectedBuoys.forEach(b => (selectedSensorsByBuoy[b] || []).forEach(m => s.add(m)))
    return [...s]
  }, [selectedBuoys, selectedSensorsByBuoy])

  const fieldsByType = useMemo(() => {
    const map = new Map()
    selectedBuoys.forEach(b => {
      (selectedFieldsByBuoy[b] || []).forEach(key => {
        const [m, f] = key.split(':')
        if (!map.has(m)) map.set(m, new Set())
        map.get(m).add(f)
      })
    })
    const out = {}
    for (const [m, set] of map.entries()) out[m] = [...set]
    return out
  }, [selectedBuoys, selectedFieldsByBuoy])

  useEffect(() => {
    if (!selectedBuoys.length) {
      setData([])
      return
    }
    
    // Even if no sensors are selected, fetch basic data to populate available sensors
    setLoading(true)
    ;(async () => {
      try {
        if (requestedTypes.length > 0) {
          // Fetch specific sensor data
          const results = await Promise.all(
            requestedTypes.map(type =>
              querySensorData(
                type,
                fieldsByType[type] || [],
                debouncedRange,
                null,
                selectedBuoys,
                debouncedStop
              )
            )
          )
          setData(results.flat())
        } else {
          // No sensors selected yet, don't fetch any data
          setData([])
        }
      } catch (e) {
        console.error("❌ useSensorData fetch error", e)
        setData([])
      } finally {
        setLoading(false)
      }
    })()
  }, [requestedTypes.join('|'), JSON.stringify(fieldsByType), debouncedRange, debouncedStop, selectedBuoys.join('|')])

  const available = useMemo(() => {
    const buoys = new Set(), sensors = new Set()
    const fieldsBySensor = new Map()
    for (const d of data) {
      if (d.buoy_id) buoys.add(d.buoy_id)
      sensors.add(d._measurement)
      if (!fieldsBySensor.has(d._measurement)) fieldsBySensor.set(d._measurement, new Set())
      fieldsBySensor.get(d._measurement).add(d._field)
    }
    return { buoys: [...buoys], sensors: [...sensors], fieldsBySensor }
  }, [data])

  const filtered = useMemo(() => {
    const setBuoys = new Set(selectedBuoys)
    const sensorsByBuoy = new Map(Object.entries(selectedSensorsByBuoy).map(([b, arr]) => [b, new Set(arr)]))
    return data.filter(d => {
      if (!setBuoys.has(d.buoy_id)) return false
      const chosen = sensorsByBuoy.get(d.buoy_id)
      return !chosen || chosen.size === 0 || chosen.has(d._measurement)
    })
  }, [data, selectedBuoys, selectedSensorsByBuoy])

  return { loading, all: data, filtered, available }
}
